import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { PlatformStats } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui.js';
import { MiniChart } from '../../components/MiniChart.js';
import { AdminCard, Stat } from './parts.js';

/**
 * ADM-008 — the platform's own numbers.
 *
 * Everything here is an aggregate. Staff see that a business wrote forty
 * quotes; they do not see one of them. That is not a limitation of the screen —
 * it is the same rule the rest of module 13 is built on, and the reason the
 * back office can exist at all under the Privacy Protection Regulations.
 */
export function ReportsSection() {
  const { t } = useTranslation();

  const stats = useQuery({
    queryKey: ['platform', 'stats'],
    queryFn: () => api.get<PlatformStats>('/platform/stats'),
  });

  if (stats.isLoading || !stats.data) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const data = stats.data;
  const shekels = (agorot: number) => `₪${(agorot / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Stat label={t('admin.reports.mrr')} value={shekels(data.totals.mrrAgorot)} tone="good" />
        <Stat label={t('admin.reports.quotes')} value={data.totals.quotes} />
        <Stat label={t('admin.reports.approved')} value={data.totals.quotesApproved} tone="info" />
        <Stat label={t('admin.reports.inquiries')} value={data.totals.inquiries} tone="muted" />
      </div>

      <AdminCard title={t('admin.reports.months')}>
        <MiniChart
          categories={data.months.map((month) => month.month.slice(2))}
          ariaLabel={t('admin.reports.months')}
          series={[
            {
              key: 'signups',
              label: t('admin.reports.signups'),
              color: 'hsl(var(--primary))',
              values: data.months.map((month) => month.signups),
            },
            {
              key: 'quotes',
              label: t('admin.reports.quotes'),
              color: 'hsl(var(--info))',
              values: data.months.map((month) => month.quotes),
            },
            {
              key: 'invoices',
              label: t('admin.reports.invoices'),
              color: 'hsl(var(--success))',
              values: data.months.map((month) => month.invoices),
            },
          ]}
        />
      </AdminCard>

      <AdminCard title={t('admin.reports.planMix')} hint={t('admin.reports.payingOnly')}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-start font-medium py-2">{t('platform.plan')}</th>
                <th className="text-end font-medium py-2">{t('admin.reports.businesses')}</th>
                <th className="text-end font-medium py-2">{t('admin.reports.revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {data.planMix.map((entry) => (
                <tr key={entry.plan} className="border-t border-border">
                  <td className="py-2.5">{t(`billing.plans.${entry.plan}`)}</td>
                  <td className="py-2.5 text-end ltr-nums">{entry.businesses}</td>
                  <td className="py-2.5 text-end ltr-nums">{shekels(entry.monthlyAgorot)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
}
